import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Pencil, TicketPercent, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, PageLoader } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { formatDate, formatIdr, formatRelative } from '@/lib/format';

/**
 * Pengelolaan kode diskon.
 *
 * Dua hal yang perlu diingat saat mengubah halaman ini:
 *
 *   1. Kode TIDAK bisa diubah setelah dibuat. Kode yang sudah tersebar di
 *      poster atau pesan berantai akan berhenti bekerja tanpa ada yang tahu
 *      sebabnya, dan catatan pemakaian lama jadi menunjuk kode yang berbeda.
 *   2. Pemakaian dihitung saat pembayaran LUNAS, bukan saat kode dimasukkan.
 *      Angka pemakaian di sini karena itu selalu berarti "benar-benar
 *      terpakai", bukan "pernah dicoba".
 */

const KOSONG = {
  code: '',
  description: '',
  type: 'percent',
  value: '10',
  maxDiscountIdr: '0',
  minPurchaseIdr: '0',
  scope: 'all',
  maxRedemptions: '0',
  maxPerOrganization: '1',
  expiresAt: '',
  active: true,
};

const NAMA_CAKUPAN = { all: 'Semua', plan: 'Paket saja', addon: 'Add-on saja' };

/** Tanggal untuk isian `date`, yang menuntut bentuk YYYY-MM-DD. */
const keIsianTanggal = (nilai) => (nilai ? new Date(nilai).toISOString().slice(0, 10) : '');

const DialogKupon = ({ awal, onSimpan, menyimpan, onClose }) => {
  const [f, setF] = useState(() =>
    awal
      ? {
          code: awal.code,
          description: awal.description ?? '',
          type: awal.type,
          value: String(awal.value),
          maxDiscountIdr: String(awal.maxDiscountIdr ?? 0),
          minPurchaseIdr: String(awal.minPurchaseIdr ?? 0),
          scope: awal.scope,
          maxRedemptions: String(awal.maxRedemptions ?? 0),
          maxPerOrganization: String(awal.maxPerOrganization ?? 1),
          expiresAt: keIsianTanggal(awal.expiresAt),
          active: awal.active,
        }
      : KOSONG,
  );

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const persen = f.type === 'percent';

  const kirim = () => {
    const badan = {
      description: f.description.trim(),
      type: f.type,
      value: Number(f.value),
      maxDiscountIdr: Number(f.maxDiscountIdr),
      minPurchaseIdr: Number(f.minPurchaseIdr),
      scope: f.scope,
      maxRedemptions: Number(f.maxRedemptions),
      maxPerOrganization: Number(f.maxPerOrganization),
      expiresAt: f.expiresAt ? new Date(`${f.expiresAt}T23:59:59`) : null,
      active: f.active,
    };
    // Kode hanya dikirim saat membuat baru. Saat menyunting ia diabaikan
    // server, tetapi lebih jelas kalau tidak dikirim sama sekali.
    onSimpan(awal ? badan : { ...badan, code: f.code.trim().toUpperCase() });
  };

  const sah = (awal || f.code.trim().length >= 3) && Number(f.value) > 0 && (!persen || Number(f.value) <= 100);

  return (
    <Modal
      open
      onClose={onClose}
      title={awal ? `Ubah ${awal.code}` : 'Kode diskon baru'}
      size="lg"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Batal
          </Button>
          <Button loading={menyimpan} disabled={!sah} onClick={kirim}>
            Simpan
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field
          label="Kode"
          required={!awal}
          hint={
            awal
              ? 'Kode tidak bisa diubah. Kode yang sudah tersebar akan berhenti bekerja tanpa ada yang tahu sebabnya.'
              : 'Huruf besar semua. Pembeli boleh mengetiknya dengan huruf kecil.'
          }
        >
          {(props) => (
            <Input
              {...props}
              value={f.code}
              disabled={Boolean(awal)}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
              placeholder="HEMAT20"
              className="font-mono"
            />
          )}
        </Field>

        <Field label="Keterangan" hint="Untuk catatan internal, tidak ditampilkan ke pembeli.">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={f.description}
              onChange={(e) => set('description', e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Bentuk potongan" required>
            {(props) => (
              <Select {...props} value={f.type} onChange={(e) => set('type', e.target.value)}>
                <option value="percent">Persen</option>
                <option value="amount">Nominal rupiah</option>
              </Select>
            )}
          </Field>

          <Field label={persen ? 'Besar potongan (persen)' : 'Besar potongan (Rp)'} required>
            {(props) => (
              <Input
                {...props}
                type="number"
                min="1"
                max={persen ? '100' : undefined}
                value={f.value}
                onChange={(e) => set('value', e.target.value)}
              />
            )}
          </Field>

          <Field label="Berlaku untuk">
            {(props) => (
              <Select {...props} value={f.scope} onChange={(e) => set('scope', e.target.value)}>
                <option value="all">Semua</option>
                <option value="plan">Paket saja</option>
                <option value="addon">Add-on saja</option>
              </Select>
            )}
          </Field>
        </div>

        {persen && (
          <Field
            label="Batas potongan maksimum (Rp)"
            hint="Nol berarti tanpa batas. Diskon 50 persen tanpa batas pada paket termahal berarti kehilangan puluhan ribu sekali klik."
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                value={f.maxDiscountIdr}
                onChange={(e) => set('maxDiscountIdr', e.target.value)}
              />
            )}
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Minimum belanja (Rp)" hint="Nol berarti tanpa minimum.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                value={f.minPurchaseIdr}
                onChange={(e) => set('minPurchaseIdr', e.target.value)}
              />
            )}
          </Field>
          <Field label="Kuota total" hint="Nol berarti tanpa batas.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                value={f.maxRedemptions}
                onChange={(e) => set('maxRedemptions', e.target.value)}
              />
            )}
          </Field>
          <Field label="Maksimal per akun">
            {(props) => (
              <Input
                {...props}
                type="number"
                min="1"
                value={f.maxPerOrganization}
                onChange={(e) => set('maxPerOrganization', e.target.value)}
              />
            )}
          </Field>
        </div>

        <Field label="Berlaku sampai" hint="Kosongkan berarti tanpa batas waktu.">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={f.expiresAt}
              onChange={(e) => set('expiresAt', e.target.value)}
            />
          )}
        </Field>

        <Toggle
          checked={f.active}
          onChange={(v) => set('active', v)}
          label="Aktif"
          description="Kalau dimatikan, kode langsung ditolak saat dipakai."
        />
      </div>
    </Modal>
  );
};

const DialogRiwayat = ({ kupon, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['coupon-redemptions', kupon.id],
    queryFn: () => api.get(`/platform/coupons/${kupon.id}/redemptions`),
  });

  const items = data?.data ?? [];

  return (
    <Modal open onClose={onClose} title={`Pemakaian ${kupon.code}`} size="lg">
      {isLoading ? (
        <PageLoader label="Memuat riwayat..." />
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon={History}
          title="Belum pernah dipakai"
          description="Pemakaian dicatat saat pembayaran lunas, bukan saat kode dimasukkan."
        />
      ) : (
        <TableWrapper>
          <Table>
            <thead>
              <Tr>
                <Th>Organisasi</Th>
                <Th>Potongan</Th>
                <Th>Waktu</Th>
              </Tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <Tr key={r._id ?? r.id}>
                  <Td>{r.organizationId?.name ?? '(tidak diketahui)'}</Td>
                  <Td className="tabular">{formatIdr(r.discountIdr)}</Td>
                  <Td className="text-[var(--text-muted)]">{formatRelative(r.createdAt)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </Modal>
  );
};

export const CouponsAdminPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [menyunting, setMenyunting] = useState(null);
  const [riwayat, setRiwayat] = useState(null);
  const [hapusTarget, setHapusTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['platform-coupons'],
    queryFn: () => api.get('/platform/coupons'),
  });

  const segarkan = () => queryClient.invalidateQueries({ queryKey: ['platform-coupons'] });

  const simpan = useMutation({
    mutationFn: (body) =>
      menyunting?.id
        ? api.patch(`/platform/coupons/${menyunting.id}`, body)
        : api.post('/platform/coupons', body),
    onSuccess: () => {
      toast.success(menyunting?.id ? 'Kode diperbarui' : 'Kode dibuat');
      setMenyunting(null);
      segarkan();
    },
    onError: (err) =>
      toast.error('Gagal menyimpan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const hapus = useMutation({
    mutationFn: () => api.delete(`/platform/coupons/${hapusTarget.id}`),
    onSuccess: (res) => {
      toast.success('Kode dinonaktifkan', { description: res?.data?.message });
      setHapusTarget(null);
      segarkan();
    },
    onError: (err) => {
      toast.error('Gagal menghapus', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      });
      setHapusTarget(null);
    },
  });

  if (isLoading) return <PageLoader label="Memuat kode diskon..." />;

  const items = data?.data ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Kode Diskon</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Potongan dihitung server saat pembeli memasukkan kodenya, dan pemakaian dicatat saat
            pembayaran lunas. Kode yang cuma dicoba lalu ditinggalkan tidak menghabiskan kuota.
          </p>
        </div>
        <Button icon={TicketPercent} onClick={() => setMenyunting({})}>
          Kode Baru
        </Button>
      </div>

      <Card>
        {items.length === 0 ? (
          <EmptyState
            icon={TicketPercent}
            title="Belum ada kode diskon"
            description="Buat satu untuk promosi peluncuran atau pemulihan pelanggan yang berhenti."
          />
        ) : (
          <CardBody className="space-y-2">
            {items.map((k) => {
              const habis = k.maxRedemptions > 0 && k.redemptions >= k.maxRedemptions;
              const lewat = k.expiresAt && new Date(k.expiresAt) < new Date();

              return (
                <div
                  key={k.id}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--border-subtle)] px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2">
                      <code className="font-mono text-sm font-bold text-[var(--text-primary)]">
                        {k.code}
                      </code>
                      {!k.active && (
                        <Badge tone="neutral" size="sm">
                          Nonaktif
                        </Badge>
                      )}
                      {habis && (
                        <Badge tone="warning" size="sm">
                          Kuota habis
                        </Badge>
                      )}
                      {lewat && (
                        <Badge tone="danger" size="sm">
                          Kedaluwarsa
                        </Badge>
                      )}
                    </p>
                    <p className="truncate text-xs text-[var(--text-muted)]">
                      {k.type === 'percent' ? `${k.value}%` : formatIdr(k.value)} ·{' '}
                      {NAMA_CAKUPAN[k.scope]}
                      {k.minPurchaseIdr > 0 && ` · min ${formatIdr(k.minPurchaseIdr)}`}
                      {k.maxDiscountIdr > 0 && ` · maks ${formatIdr(k.maxDiscountIdr)}`}
                      {k.expiresAt && ` · sampai ${formatDate(k.expiresAt)}`}
                    </p>
                  </div>

                  <span className="text-sm text-[var(--text-secondary)]">
                    {k.redemptions} dipakai
                    {k.maxRedemptions > 0 && ` dari ${k.maxRedemptions}`}
                  </span>

                  <Button
                    variant="ghost"
                    size="sm"
                    icon={History}
                    iconOnly
                    aria-label={`Riwayat pemakaian ${k.code}`}
                    onClick={() => setRiwayat(k)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Pencil}
                    iconOnly
                    aria-label={`Ubah ${k.code}`}
                    onClick={() => setMenyunting(k)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Trash2}
                    iconOnly
                    aria-label={`Hapus ${k.code}`}
                    className="text-danger-500 hover:text-danger-600"
                    onClick={() => setHapusTarget(k)}
                  />
                </div>
              );
            })}
          </CardBody>
        )}
      </Card>

      {menyunting && (
        <DialogKupon
          awal={menyunting.id ? menyunting : null}
          menyimpan={simpan.isPending}
          onSimpan={(body) => simpan.mutate(body)}
          onClose={() => setMenyunting(null)}
        />
      )}

      {riwayat && <DialogRiwayat kupon={riwayat} onClose={() => setRiwayat(null)} />}

      <ConfirmDialog
        open={Boolean(hapusTarget)}
        onClose={() => setHapusTarget(null)}
        onConfirm={() => hapus.mutate()}
        loading={hapus.isPending}
        destructive
        title={`Nonaktifkan kode "${hapusTarget?.code}"?`}
        description="Kode langsung berhenti bekerja. Riwayat pemakaiannya tetap tersimpan."
        confirmLabel="Ya, nonaktifkan"
      />
    </div>
  );
};
